import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Lock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'multi_select' | 'currency' | 'attachment'

type Field = {
  id: string
  field_type: FieldType
  label: string
  identifier: string
  required: boolean
  encrypted: boolean
  description?: string
  position: number
  options: { id: string; label: string; value: string; position: number }[]
  config: Record<string, unknown>
}

type FieldValueData = {
  field_id: string
  field_type: FieldType
  value: string | number | null
  option_ids: string[]
}

interface DynamicFormProps {
  fields: Field[]
  valuesApiPath: string
  submitLabel?: string
  cancelLabel?: string
  onCancel?: () => void
  onSubmit?: () => void
}

export function DynamicForm({
  fields,
  valuesApiPath,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  onCancel,
  onSubmit,
}: DynamicFormProps) {
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValueData>>(() => {
    const initial: Record<string, FieldValueData> = {}
    fields.forEach(f => {
      initial[f.id] = {
        field_id: f.id,
        field_type: f.field_type,
        value: null,
        option_ids: [],
      }
    })
    return initial
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const updateValue = (fieldId: string, value: string | number | null) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], value },
    }))
  }

  const updateOptionIds = (fieldId: string, optionIds: string[]) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], option_ids: optionIds },
    }))
  }

  const handleSelectChange = (fieldId: string, selectedValue: string) => {
    updateValue(fieldId, selectedValue)
  }

  const handleMultiSelectChange = (fieldId: string, optionId: string, checked: boolean) => {
    const current = fieldValues[fieldId]?.option_ids || []
    const newIds = checked
      ? [...current, optionId]
      : current.filter(id => id !== optionId)
    updateOptionIds(fieldId, newIds)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const payload = {
      values: Object.values(fieldValues).map(fv => ({
        field_id: fv.field_id,
        field_type: fv.field_type,
        value: fv.value,
        option_ids: fv.option_ids,
      })),
    }

    router.post(valuesApiPath, payload, {
      onSuccess: () => {
        setIsSubmitting(false)
        onSubmit?.()
      },
      onError: () => {
        setIsSubmitting(false)
      },
    })
  }

  const sortedFields = [...fields].sort((a, b) => a.position - b.position)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {sortedFields.map(field => (
        <div key={field.id} className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
            </Label>
            {field.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
            {field.encrypted && <Lock className="h-3 w-3 text-muted-foreground" />}
          </div>

          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}

          {renderField(field, fieldValues[field.id], updateValue, handleSelectChange, handleMultiSelectChange)}
        </div>
      ))}

      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </form>
  )
}

function renderField(
  field: Field,
  fieldValue: FieldValueData | undefined,
  updateValue: (id: string, value: string | number | null) => void,
  onSelectChange: (id: string, value: string) => void,
  onMultiSelectChange: (id: string, optionId: string, checked: boolean) => void
) {
  const placeholder = field.config?.placeholder as string | undefined

  switch (field.field_type) {
    case 'text':
    case 'email':
    case 'phone':
      return (
        <Input
          id={field.id}
          type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
          placeholder={placeholder}
          value={(fieldValue?.value as string) || ''}
          onChange={e => updateValue(field.id, e.target.value || null)}
          className="h-9"
        />
      )

    case 'textarea':
      return (
        <Textarea
          id={field.id}
          placeholder={placeholder}
          value={(fieldValue?.value as string) || ''}
          onChange={e => updateValue(field.id, e.target.value || null)}
          className="min-h-20"
        />
      )

    case 'number':
      return (
        <Input
          id={field.id}
          type="number"
          placeholder={placeholder}
          value={(fieldValue?.value as number) ?? ''}
          onChange={e => updateValue(field.id, e.target.value ? parseFloat(e.target.value) : null)}
          className="h-9"
        />
      )

    case 'date':
      return (
        <Input
          id={field.id}
          type="date"
          value={(fieldValue?.value as string) || ''}
          onChange={e => updateValue(field.id, e.target.value || null)}
          className="h-9"
        />
      )

    case 'currency':
      return (
        <div className="flex gap-2">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
            {(field.config?.currency_code as string) || 'USD'}
          </span>
          <Input
            id={field.id}
            type="number"
            placeholder="0.00"
            value={(fieldValue?.value as number) ?? ''}
            onChange={e => updateValue(field.id, e.target.value ? parseFloat(e.target.value) : null)}
            className="h-9 flex-1"
          />
        </div>
      )

    case 'select':
      return (
        <Select
          value={(fieldValue?.value as string) || ''}
          onValueChange={value => onSelectChange(field.id, value)}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map(opt => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )

    case 'multi_select':
      return (
        <div className="space-y-2 border rounded-md p-3">
          {field.options.map(opt => (
            <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={fieldValue?.option_ids?.includes(opt.id) || false}
                onCheckedChange={checked => onMultiSelectChange(field.id, opt.id, !!checked)}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      )

    case 'attachment':
      return (
        <Input
          id={field.id}
          type="file"
          className="h-9"
          multiple={((field.config?.max_files as number) || 1) > 1}
        />
      )

    default:
      return (
        <Input
          id={field.id}
          value={(fieldValue?.value as string) || ''}
          onChange={e => updateValue(field.id, e.target.value || null)}
          className="h-9"
        />
      )
  }
}
